import useApp from "hooks/useApp";

import { FeaturesSection } from "./components/FeaturesSection";
import { ForBusinessesSection } from "./components/ForBusinessesSection";
import { memo, useEffect, useRef } from "react";

import styles from "./megaMenu.module.scss";

/**
 * MegaMenu component represents the dropdown menu
 * that appears when the user hovers over the main menu items.
 */

interface MegaMenuI extends React.HTMLAttributes<HTMLDivElement> {
  shownSection: "features" | "for-businesses";
  setShowMegaMenuSection: React.Dispatch<
    React.SetStateAction<"features" | "for-businesses" | "">
  >;
}

// Export for testing purposes:
export const MegaMenu = (props: MegaMenuI) => {
  const { shownSection, setShowMegaMenuSection, ...restProps } = props;

  const { theme } = useApp();

  // Show the menu only if the section is provided:
  const showMenu = !!shownSection;

  const isTransparentHeaderPage = document.querySelector(
    ".show-transparent-header"
  );

  // logic to hide the tooltip when the mega menu is shown
  const prevZIndex = useRef<number | null>(null);

  useEffect(() => {
    const homeTooltip = document.querySelector(".dropzone-tooltip-container");
    if (homeTooltip) {
      if (showMenu) {
        prevZIndex.current = parseInt(homeTooltip.style.zIndex);
        homeTooltip.style.zIndex = "0";
      } else {
        homeTooltip.style.zIndex = prevZIndex.current?.toString() || "";
      }
    }
  }, [shownSection]);

  return (
    <div
      id="mega-menu"
      className={`${styles.wrapper} ${styles[theme]} ${
        showMenu ? styles.expandedMenu : styles.collapsedMenu
      } ${isTransparentHeaderPage ? styles.transparentHeader : ""}`}
      data-testid="mega-menu"
      {...restProps}
    >
      <FeaturesSection
        setShowMegaMenuSection={setShowMegaMenuSection}
        style={{
          // Inline styles allows to render all sections at once and then show/hide them
          // instead of rendering them on demand in order to prevent flickering.
          opacity: shownSection === "features" ? 1 : 0,
          pointerEvents: shownSection === "features" ? "auto" : "none",
          height: shownSection === "features" ? "auto" : 0,
          position: shownSection === "features" ? "relative" : "absolute",
        }}
      />
      <ForBusinessesSection
        style={{
          opacity: shownSection === "for-businesses" ? 1 : 0,
          pointerEvents: shownSection === "for-businesses" ? "auto" : "none",
          height: shownSection === "for-businesses" ? "auto" : 0,
          position: shownSection === "for-businesses" ? "relative" : "absolute",
        }}
      />
    </div>
  );
};

// Export default for use in the application:
export default memo(MegaMenu);
